import React, { useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import { Button, ConfigProvider, Input, Modal } from 'antd-v5';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import MergeFieldRow from './MergeFieldRow';
import normalizeClientName from './normalizeClientName';
import formatPhoneNumber from '../../../../utils/formatPhoneNumber';

const ALWAYS_SHOWN_FIELDS = [
  { key: 'name', label: 'NAME' },
  { key: 'posRefNum', label: 'POS' },
  { key: 'mobile', label: 'PHONE' },
  { key: 'email', label: 'EMAIL' },
];

const CONDITIONAL_FIELDS = [
  { key: 'storeName', label: 'STORE' },
  { key: 'assignedAssociate', label: 'ASSOCIATE' },
  { key: 'spouseName', label: 'SPOUSE' },
  { key: 'birthday', label: 'BIRTHDAY' },
  { key: 'anniversary', label: 'ANNIVERSARY' },
];

// The API sometimes returns the literal string "null" for empty fields.
// Coerce it to a real null so it's treated as empty everywhere.
const coerceNull = val => (val === 'null' ? null : val);

const formatLifeEventDate = val => {
  if (!val) return null;
  const str = String(val);
  if (str.length === 4) return moment(str, 'MMDD').format('MMMM DD');
  return str;
};

const formatDisplayValue = (key, val) => {
  if (!val) return null;
  if (key === 'name') return normalizeClientName(val);
  if (key === 'mobile') return formatPhoneNumber(val, 'b');
  if (key === 'birthday' || key === 'anniversary') return formatLifeEventDate(val);
  return val;
};

const getFieldValue = (client, key) => {
  if (key === 'assignedAssociate') {
    const first = client?.assignedAssociateFirstName;
    const last = client?.assignedAssociateLastName;
    if (first || last) return [first, last].filter(Boolean).join(' ');
    return null;
  }
  return coerceNull(client?.[key]);
};

const MergeReviewStep = ({ keptClient, sourceClient, onBack, onMerge }) => {
  const [overrides, setOverrides] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');

  const handleCopy = useCallback((fieldKey, value) => {
    setOverrides(prev => ({ ...prev, [fieldKey]: value }));
  }, []);

  const handleUndo = useCallback(fieldKey => {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  }, []);

  // Only show conditional fields when both clients have a value and they differ —
  // if only one side has a value, the merge auto-resolves and no user choice is needed
  const visibleConditionalFields = useMemo(() => {
    return CONDITIONAL_FIELDS.filter(field => {
      const keptVal = getFieldValue(keptClient, field.key);
      const sourceVal = getFieldValue(sourceClient, field.key);
      return !!keptVal && !!sourceVal && keptVal !== sourceVal;
    });
  }, [keptClient, sourceClient]);

  const allFields = useMemo(() => [...ALWAYS_SHOWN_FIELDS, ...visibleConditionalFields], [visibleConditionalFields]);

  // Compute auto-applied POS display: if kept has no POS but source does, it transfers automatically
  const posAutoApplied = !keptClient?.posRefNum && !!sourceClient?.posRefNum;

  // Build the final merged field values from the kept client, applying any user overrides from the source
  // posRefNum is intentionally excluded — the backend handles POS assignment automatically
  const buildMergePayload = useCallback(() => {
    const merged = {
      name: keptClient?.name,
      mobile: coerceNull(keptClient?.mobile) || null,
      email: coerceNull(keptClient?.email) || null,
      storeId: keptClient?.storeId,
      assignedAssociateId: keptClient?.assignedAssociateId || null,
      spouseId: keptClient?.spouseId || null,
      birthday: coerceNull(keptClient?.birthday) || null,
      anniversary: coerceNull(keptClient?.anniversary) || null,
    };

    // Apply overrides — use the source client's raw API values for overridden fields
    Object.keys(overrides).forEach(key => {
      if (key === 'storeName') {
        merged.storeId = sourceClient?.storeId;
      } else if (key === 'assignedAssociate') {
        merged.assignedAssociateId = sourceClient?.assignedAssociateId;
      } else if (key === 'spouseName') {
        merged.spouseId = sourceClient?.spouseId;
      } else {
        merged[key] = coerceNull(sourceClient?.[key]) || null;
      }
    });

    return merged;
  }, [keptClient, sourceClient, overrides]);

  const handlePermanentlyMerge = useCallback(() => {
    setShowConfirm(true);
    setConfirmInput('');
  }, []);

  const handleConfirmMerge = useCallback(() => {
    setShowConfirm(false);
    if (onMerge) {
      onMerge({
        keptClientId: keptClient?.client_id || keptClient?.id,
        archivedClientId: sourceClient?.client_id || sourceClient?.id,
        data: buildMergePayload(),
      });
    }
  }, [keptClient, sourceClient, buildMergePayload, onMerge]);

  const keptName = normalizeClientName(keptClient?.name) || 'Unknown';
  const archivedName = normalizeClientName(sourceClient?.name) || 'Unknown';
  const keptId = keptClient?.client_id || keptClient?.id;
  const archivedId = sourceClient?.client_id || sourceClient?.id;
  const confirmMatches = normalizeClientName(confirmInput).toLowerCase() === keptName.toLowerCase();

  return (
    <div>
      <h2 className="merge-modal-title">Review your merged client</h2>
      <p className="merge-modal-description">
        We merge as much information as possible from both client profiles. If the two profiles have different values
        for the same field, the saved client profile wins. Merged data includes: Associate Assignment, Addresses, Life
        Events, Relationships, Tags, Preferences, Sales Opportunities, Client Activities, Collections, Chat Messages,
        and Reminders.
      </p>

      <div className="merge-review-columns">
        {/* Left column header */}
        <div className="merge-review-col">
          <h3 className="merge-review-col-header">Keep This Client</h3>
          <p className="merge-review-col-subtitle">This is the client profile you&apos;re on right now</p>
        </div>

        {/* Center spacer for arrow column header */}
        <div className="merge-review-arrows" />

        {/* Right column header */}
        <div className="merge-review-col">
          <h3 className="merge-review-col-header">Archive This Client</h3>
          <p className="merge-review-col-subtitle">This is the client you searched and selected</p>
        </div>

        {/* Field rows */}
        {allFields.map(field => {
          const isPosField = field.key === 'posRefNum';
          return (
            <MergeFieldRow
              key={field.key}
              label={field.label}
              keptValue={
                isPosField && posAutoApplied
                  ? formatDisplayValue(field.key, getFieldValue(sourceClient, field.key))
                  : formatDisplayValue(field.key, getFieldValue(keptClient, field.key))
              }
              sourceValue={formatDisplayValue(field.key, getFieldValue(sourceClient, field.key))}
              isOverridden={isPosField ? posAutoApplied : field.key in overrides}
              onCopy={() => handleCopy(field.key, getFieldValue(sourceClient, field.key))}
              onUndo={() => handleUndo(field.key)}
              readOnly={isPosField}
            />
          );
        })}
      </div>

      <div className="merge-review-footer">
        <Button onClick={onBack}>Back</Button>
        <Button type="primary" onClick={handlePermanentlyMerge} data-testid="clients__merge--review-merge-button">
          Permanently Merge
        </Button>
      </div>

      <ConfigProvider prefixCls="ant5" theme={{ token: { colorPrimary: '#3AA4FC' } }}>
        <Modal
          prefixCls="ant5-modal"
          open={showConfirm}
          onCancel={() => setShowConfirm(false)}
          afterClose={() => setConfirmInput('')}
          footer={null}
          title={
            <span>
              <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
              Are you sure you want to merge clients?
            </span>
          }
        >
          <p>
            &#10003; Keep: {keptName} (ID: {keptId})
            <br />
            &#10005; Archive: {archivedName} (ID: {archivedId})
          </p>
          <p>
            This is a permanent action that can&apos;t be undone. Only continue if you are sure you want to merge these
            clients permanently.
          </p>
          <p>
            Type <strong>{keptName}</strong> to confirm:
          </p>
          <Input
            value={confirmInput}
            onChange={e => setConfirmInput(e.target.value)}
            placeholder={keptName}
            style={{ marginBottom: 16 }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setShowConfirm(false)}>Go Back</Button>
            <Button
              danger
              type="primary"
              disabled={!confirmMatches}
              onClick={handleConfirmMerge}
              data-testid="clients__merge--confirm-merge-button"
            >
              Permanently Merge
            </Button>
          </div>
        </Modal>
      </ConfigProvider>
    </div>
  );
};

export default MergeReviewStep;
